import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';

import { Link } from 'react-router-dom';

import './stylesheet.css';

import DaystartPrompt from '../DaystartPrompt';
import ExternalLink from '../UI/ExternalLink';

import { bindActionCreators } from 'redux';

import * as baseActions from '../../actions/base';

import {
  DASHBOARD_DEFAULT_ICON,
  dashboardConfigFromState,
  defaultDashboardConfig,
  filePathToUrl,
  formatDatePath,
  isExternalLink,
  normalizeDashboardConfig,
  serializeDashboardConfig,
} from '../../lib/dashboard';

const ORG_FILE_PATHS_DATALIST_ID = 'dashboard-org-file-paths';

// `onClick` lets a tile handle its own navigation (the daily note has to check
// whether the file exists first). The href is kept either way, so middle-click and
// "open in new tab" still work and the tile stays a real link.
const Tile = ({ icon, label, path, note, onClick }) => {
  const contents = (
    <>
      <i className={`fas fa-${icon} fa-lg dashboard__tile__icon`} />
      <span className="dashboard__tile__label">{label}</span>
      <span className="dashboard__tile__path">{note || path}</span>
    </>
  );

  // A button may point at a URL rather than an Org file. That is a real link out of
  // the app, so it opens in a new window and must not go through the router, which
  // would turn 'https://example.org' into '/file/https://example.org'.
  if (isExternalLink(path)) {
    return (
      <ExternalLink className="dashboard__tile" href={path.trim()}>
        {contents}
      </ExternalLink>
    );
  }

  return (
    <Link
      className="dashboard__tile"
      to={filePathToUrl(path)}
      onClick={
        onClick &&
        ((event) => {
          event.preventDefault();
          onClick();
        })
      }
    >
      {contents}
    </Link>
  );
};

const UnconfiguredTile = ({ index, onConfigure }) => (
  <button className="dashboard__tile dashboard__tile--empty" onClick={onConfigure}>
    <i className="fas fa-plus fa-lg dashboard__tile__icon" />
    <span className="dashboard__tile__label">Button {index + 1}</span>
    <span className="dashboard__tile__path">not configured - tap to set up</span>
  </button>
);

const ButtonEditor = ({ index, button, onChange }) => (
  <div className="dashboard__editor__row">
    <div className="dashboard__editor__row__title">
      <i className={`fas fa-${button.icon || DASHBOARD_DEFAULT_ICON}`} /> Button {index + 1}
    </div>

    <label className="dashboard__editor__field">
      <span className="dashboard__editor__field__label">Label</span>
      <input
        className="textfield"
        type="text"
        value={button.label}
        placeholder="Inbox"
        onChange={(event) => onChange(index, 'label', event.target.value)}
      />
    </label>

    <label className="dashboard__editor__field">
      <span className="dashboard__editor__field__label">File or URL</span>
      <input
        className="textfield"
        type="text"
        value={button.path}
        placeholder="/roam/inbox.org"
        list={ORG_FILE_PATHS_DATALIST_ID}
        onChange={(event) => onChange(index, 'path', event.target.value)}
      />
    </label>

    <label className="dashboard__editor__field">
      <span className="dashboard__editor__field__label">Icon</span>
      <input
        className="textfield"
        type="text"
        value={button.icon}
        placeholder={DASHBOARD_DEFAULT_ICON}
        onChange={(event) => onChange(index, 'icon', event.target.value)}
      />
    </label>
  </div>
);

const Dashboard = ({ orgFilePaths, storedConfig, shouldStoreSettingsInSyncBackend, base }) => {
  // The configuration is a setting, so it comes from the store: whatever was loaded
  // from /.organice-config.json on startup, or the built-in defaults.
  const config = useMemo(() => dashboardConfigFromState(storedConfig), [storedConfig]);
  // A non-null draft means the editor is open.
  const [draft, setDraft] = useState(null);
  const [exportedConfig, setExportedConfig] = useState(null);
  const [copyConfirmation, setCopyConfirmation] = useState(null);
  // Set while the daily note is being opened: it may not exist yet, in which case
  // <DaystartPrompt> offers to create it from snippets.
  const [daystartTargetPath, setDaystartTargetPath] = useState(null);

  const today = useMemo(() => new Date(), []);
  const dailyPath = formatDatePath(config.dailyPathTemplate, today);

  const handleEditClick = () => {
    setExportedConfig(null);
    setDraft(config);
  };

  const handleCancelClick = () => setDraft(null);

  // Saving hands the configuration to the settings machinery, which caches it in
  // localStorage and — with "Store settings in sync backend" on — writes it to
  // /.organice-config.json, so the buttons come back on the next device.
  const handleSaveClick = () => {
    base.setDashboardConfig(normalizeDashboardConfig(draft));
    setDraft(null);
  };

  const handleResetClick = () => {
    if (!window.confirm('Discard your buttons and use the ones built into this organice?')) {
      return;
    }

    base.setDashboardConfig(defaultDashboardConfig());
    setDraft(null);
    setExportedConfig(null);
  };

  const handleExportClick = () => {
    setCopyConfirmation(null);
    setExportedConfig(serializeDashboardConfig(draft || config));
  };

  const handleCopyClick = () => {
    if (!navigator.clipboard) {
      setCopyConfirmation('Clipboard unavailable - select the text above and copy it manually.');
      return;
    }

    navigator.clipboard.writeText(exportedConfig).then(
      () => setCopyConfirmation('Copied. Paste it into src/dashboard-config.json and commit.'),
      () => setCopyConfirmation('Copying failed - select the text above and copy it manually.')
    );
  };

  const handleFieldChange = (field) => (event) =>
    setDraft({ ...draft, [field]: event.target.value });

  const handleButtonChange = (index, field, value) =>
    setDraft({
      ...draft,
      buttons: draft.buttons.map((button, buttonIndex) =>
        buttonIndex === index ? { ...button, [field]: value } : button
      ),
    });

  return (
    <div className="dashboard-container">
      <datalist id={ORG_FILE_PATHS_DATALIST_ID}>
        {orgFilePaths.map((path) => (
          <option key={path} value={path} />
        ))}
      </datalist>

      {!!daystartTargetPath && (
        <DaystartPrompt
          targetPath={daystartTargetPath}
          onClose={() => setDaystartTargetPath(null)}
        />
      )}

      <div className="dashboard__section-title">
        <span>Always here</span>
        <span>
          <Link className="dashboard__section-title__action" to="/dashboard/contract">
            <i className="fas fa-file-signature" /> Contract
          </Link>{' '}
          <Link className="dashboard__section-title__action" to="/dashboard/week">
            <i className="fas fa-chart-line" /> This week
          </Link>{' '}
          <Link className="dashboard__section-title__action" to="/dashboard/team">
            <i className="fas fa-users" /> Team
          </Link>{' '}
          <Link className="dashboard__section-title__action" to="/dashboard/formation">
            <i className="fas fa-seedling" /> Formation
          </Link>{' '}
          <Link className="dashboard__section-title__action" to="/dashboard/review">
            <i className="fas fa-moon" /> Review
          </Link>{' '}
          <Link className="dashboard__section-title__action" to="/roam">
            <i className="fas fa-project-diagram" /> org-roam
          </Link>
        </span>
      </div>

      <div className="dashboard__grid">
        <Tile icon="tasks" label="GTD" path={config.gtdPath} />
        <Tile
          icon="calendar-alt"
          label="Daily note"
          path={dailyPath}
          note={formatDatePath('YYYY-MM-DD', today)}
          onClick={() => setDaystartTargetPath(dailyPath)}
        />
      </div>

      <div className="dashboard__section-title">
        <span>Your buttons</span>
        <button
          className="dashboard__section-title__action"
          onClick={draft ? handleCancelClick : handleEditClick}
        >
          <i className={`fas fa-${draft ? 'times' : 'pen'}`} /> {draft ? 'Cancel' : 'Configure'}
        </button>
      </div>

      {draft ? (
        <div className="dashboard__editor">
          <label className="dashboard__editor__field">
            <span className="dashboard__editor__field__label">GTD file</span>
            <input
              className="textfield"
              type="text"
              value={draft.gtdPath}
              list={ORG_FILE_PATHS_DATALIST_ID}
              onChange={handleFieldChange('gtdPath')}
            />
          </label>

          <label className="dashboard__editor__field">
            <span className="dashboard__editor__field__label">Daily note</span>
            <input
              className="textfield"
              type="text"
              value={draft.dailyPathTemplate}
              onChange={handleFieldChange('dailyPathTemplate')}
            />
          </label>

          <div className="dashboard__editor__hint">
            YYYY, MM and DD are replaced with today's date.
          </div>

          {draft.buttons.map((button, index) => (
            <ButtonEditor key={index} index={index} button={button} onChange={handleButtonChange} />
          ))}

          <div className="dashboard__editor__hint">
            Icon names are Font Awesome 5.1 solid icons, without the <code>fa-</code> prefix.
          </div>

          <div className="dashboard__editor__hint">
            A button may point at an Org file or at an <code>http://</code> /{' '}
            <code>https://</code> address — external addresses open in a new window.
          </div>

          <div className="dashboard__editor__hint">
            {shouldStoreSettingsInSyncBackend ? (
              <>
                Saving writes your buttons to <code>/.organice-config.json</code> in the sync
                backend, so they come back on your other devices.
              </>
            ) : (
              <>
                Your buttons are saved in this browser only. Turn on{' '}
                <em>Store settings in sync backend</em> in Settings to keep them in the repository
                instead.
              </>
            )}
          </div>

          <div className="dashboard__editor__actions">
            <button className="btn" onClick={handleSaveClick}>
              <i className="fas fa-check" /> Save
            </button>
            <button className="btn-passive" onClick={handleExportClick}>
              <i className="fas fa-copy" /> Export for repository
            </button>
            <button className="btn-passive" onClick={handleResetClick}>
              <i className="fas fa-undo" /> Reset to repository
            </button>
          </div>
        </div>
      ) : (
        <div className="dashboard__grid">
          {config.buttons.map((button, index) =>
            button.path ? (
              <Tile
                key={index}
                icon={button.icon}
                label={button.label || button.path}
                path={button.path}
              />
            ) : (
              <UnconfiguredTile key={index} index={index} onConfigure={handleEditClick} />
            )
          )}
        </div>
      )}

      {exportedConfig && (
        <div className="dashboard__export">
          <div className="dashboard__section-title">src/dashboard-config.json</div>
          <pre className="dashboard__export__contents">{exportedConfig}</pre>

          <div className="dashboard__editor__actions">
            <button className="btn-passive" onClick={handleCopyClick}>
              <i className="fas fa-copy" /> Copy
            </button>
          </div>

          {copyConfirmation && <div className="dashboard__editor__hint">{copyConfirmation}</div>}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  // Offer whatever the file browser has already listed as autocompletion. This
  // deliberately doesn't request a listing of its own, so opening the dashboard
  // never disturbs the directory the file browser is showing.
  const directoryListing = state.syncBackend.get('currentFileBrowserDirectoryListing');
  const listing = directoryListing ? directoryListing.get('listing') : null;

  return {
    orgFilePaths: listing
      ? listing
          .filter((file) => !file.get('isDirectory') && file.get('name').endsWith('.org'))
          .map((file) => file.get('path'))
          .toJS()
      : [],
    storedConfig: state.base.get('dashboardConfig'),
    shouldStoreSettingsInSyncBackend: state.base.get('shouldStoreSettingsInSyncBackend'),
  };
};

const mapDispatchToProps = (dispatch) => ({
  base: bindActionCreators(baseActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
